import React, { useEffect, useState } from "react";
import { Stack, Box, Typography, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useResponsive from "../../hooks/useResponsive";
import { useNavigate } from "react-router-dom";
import Header from "../../layout/dashboard/header";
import { PATH_JOBBOARD } from "../../routes/path";
import { useMutation } from "@tanstack/react-query";
import { useNotificationService } from "../../components/notification";
import { apiGetFacilityJobs, apiGetJobBoardStats } from "../../api/jobs";
import JobBoardContent from "../../components/jobBoard/JobBoardContent";
import {
  PaginationDetails,
  PaginationDetailsDummyData,
} from "../../interface/api_request";
import { JobboardStats } from "../../interface/job_board";

const JobBoard: React.FC = () => {
  const theme = useTheme();
  const { showNotification } = useNotificationService();
  const isMedium = useResponsive("down", "md", "xl");
  const isSmall = useResponsive("down", "sm", "xl");
  const navigate = useNavigate();
  const [jobsList, setJobsList] = useState([]);
  const [filter_data, setFilter_data] = useState("");
  const [stats, setStats] = useState<JobboardStats>({
    jobStatusCounts: [{ status: "active", count: 0 }],
    totalApplicants: 0,
    totalJobs: 0,
    totalPendingReviews: 0,
    totalShortlisted: 0,
    totalUpcomingInterviews: 0,
  });

  // set up pagination state
  const [pagination_state, setPagination_state] = useState<PaginationDetails>(
    PaginationDetailsDummyData,
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First call stats
        await statsRequest.mutateAsync();
        // Then call jobs
        await jobsMutation.mutateAsync();
      } catch (error) {
        console.error("Error in sequential calls:", error);
      }
    };
    fetchData();
  }, [pagination_state?.page, filter_data]);

  const statsRequest = useMutation({
    mutationFn: () => apiGetJobBoardStats(),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          console.log("Erro");
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        // Handle stats data here
        setStats(data?.data?.data);
      }
    },
    onError: (error) => {
      showNotification("Stats fetch failed", "error");
    },
  });

  const jobsMutation = useMutation({
    mutationFn: () =>
      apiGetFacilityJobs(filter_data, `${pagination_state?.page}`),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        if (!!data?.data?.meta) {
          setPagination_state({
            hasNextPage: data?.data?.meta?.hasNextPage,
            hasPrevPage: data?.data?.meta?.hasPrevPage,
            nextPage: data?.data?.meta?.nextPage,
            page: data?.data?.meta?.page,
            pages: data?.data?.meta?.pages,
            prevPage: data?.data?.meta?.prevPage,
            reads: data?.data?.meta?.reads,
            totalDocs: data?.data?.meta?.totalDocs,
            unReads: data?.data?.meta?.unReads,
          });
        }
        if (data?.data?.data?.length > 0) {
          setJobsList(data.data.data);
        } else {
          setJobsList([]);
        }
      }
    },
    onError: (error) => {
      showNotification("Fetch process failed", "error");
    },
  });

  return (
    <>
      {/* Header Menu */}
      <Header>
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            justifyContent: "space-between",
            alignItems: "center",
            marginRight: "1rem",
            height: isMedium ? 60 : 75,
          }}
        >
          <Stack direction={"row"} alignItems={"center"} sx={{ gap: "10px" }}>
            <Typography
              variant={"subtitle1"}
              sx={{
                fontWeight: 500,
                fontFamily: "Lexend",
                color: "#2F66F4",
              }}
            >
              Job Board
            </Typography>
          </Stack>
          {!isSmall && (
            <Stack direction={"row"} sx={{ gap: 2 }}>
              <Button
                onClick={() =>
                  navigate(PATH_JOBBOARD.post, {
                    state: { defaultOutsourced: true },
                  })
                }
                sx={{
                  border: `1px solid ${theme.palette.primary.main}`,
                  padding: "4px 12px",
                  height: isMedium ? "35px" : "40px",
                  fontWeight: 700,
                  borderRadius: "12px",
                  fontSize: "13px",
                  fontFamily: "DM Sans",
                }}
              >
                Hire for me
              </Button>
              <Button
                onClick={() => navigate(PATH_JOBBOARD.post)}
                variant="contained"
                sx={{
                  padding: "4px 12px",
                  height: isMedium ? "35px" : "40px",
                  fontWeight: 700,
                  borderRadius: "12px",
                  fontSize: "13px",
                  fontFamily: "DM Sans",
                }}
              >
                Post a Job
              </Button>
            </Stack>
          )}
        </Box>
      </Header>

      <JobBoardContent
        stats={stats}
        paginationState={pagination_state}
        setPaginationState={setPagination_state}
        jobData={jobsList}
        isLoading={jobsMutation.isPending}
        setFilterData={setFilter_data}
      />
    </>
  );
};

export default JobBoard;
